import type { QualityKind } from './types';

/** A newer request of the same kind took this one's pending slot before it was sent. */
export class Superseded extends Error {
  constructor() {
    super('superseded by a newer request of the same kind');
    this.name = 'Superseded';
  }
}

type Entry = { kind: QualityKind; send: () => Promise<void>; drop: () => void };
/** A tenant's queue: whether a request is in flight, and the requests waiting, oldest first. */
type Line = { busy: boolean; pending: Entry[] };

const lines = new Map<number, Line>();

const sendNext = (tenantId: number, line: Line): void => {
  const entry = line.pending.shift();
  if (!entry) {
    line.busy = false;
    lines.delete(tenantId);
    return;
  }
  line.busy = true;
  void entry.send().then(() => sendNext(tenantId, line));
};

const abortReason = (signal: AbortSignal): unknown => signal.reason ?? new DOMException('aborted', 'AbortError');

/**
 * Run `run` in the tenant's queue: at most one quality request in flight per tenant and one pending slot per kind,
 * sent in the order they were made. A pending request rejects with {@link Superseded} when a newer one of its kind
 * replaces it, and the caller's wait ends when `signal` aborts; a request already sent runs to its end and holds the
 * tenant until `run` settles, so `run` must not end on that abort (`postTrafficQuality` does not).
 */
export const enqueueQuality = <T>(
  tenantId: number,
  kind: QualityKind,
  run: () => Promise<T>,
  signal: AbortSignal,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortReason(signal));
      return;
    }
    const line = lines.get(tenantId) ?? { busy: false, pending: [] };
    lines.set(tenantId, line);
    const onAbort = (): void => {
      const at = line.pending.indexOf(entry);
      if (at >= 0) line.pending.splice(at, 1);
      reject(abortReason(signal));
    };
    const entry: Entry = {
      kind,
      send: () =>
        new Promise<T>((sent) => sent(run()))
          .then(resolve, reject)
          .finally(() => signal.removeEventListener('abort', onAbort)),
      drop: () => {
        signal.removeEventListener('abort', onAbort);
        reject(new Superseded());
      },
    };
    const older = line.pending.findIndex((e) => e.kind === kind);
    if (older >= 0) line.pending.splice(older, 1)[0].drop();
    line.pending.push(entry);
    signal.addEventListener('abort', onAbort, { once: true });
    if (!line.busy) sendNext(tenantId, line);
  });
